import { useContext, useEffect } from "react";
import { allElements } from "constants/constants";
import { OeeSummaryDashboardContext } from "contexts/OeeSummaryDashboardContext/OeeSummaryDashboardContext";
import useOeeSummaryFilterOptions from "./useOeeSummaryFilterOptions";

/**
 * The options only hold machine centers with OEE data in the selected range, so narrowing the range can
 * leave an applied machine center that is no longer offered: every card would report no data and the
 * select would read "Select a valid option", with "Clear filters" the only way out. Drop the ids that are
 * gone - falling back to All when none survive - the same guard the Summary Report filters run on the
 * shift.
 *
 * The report hosts it rather than the filters panel: the panel is closed by default, so a saved default
 * picking machine centers the opening range has no data for would otherwise leave every card empty, with
 * no hint why, until the user happened to open Filters. The correction goes into the context filters, so
 * the report itself recovers and the fix outlives the panel being opened or closed.
 */
const useAppliedMachineCentersGuard = (): void => {
    const { filters, handleFiltersChange } = useContext(OeeSummaryDashboardContext);
    const { loading, machineCentersOptions } = useOeeSummaryFilterOptions();

    useEffect(() => {
        if (loading || filters.machineCenter.includes(allElements)) return;

        const offeredIds = new Set(machineCentersOptions.map((option) => String(option.id)));
        const offeredSelection = filters.machineCenter.filter((id) => offeredIds.has(String(id)));

        if (offeredSelection.length === filters.machineCenter.length) return;

        handleFiltersChange({
            ...filters,
            machineCenter: offeredSelection.length > 0 ? offeredSelection : [allElements],
        });
    }, [filters, handleFiltersChange, loading, machineCentersOptions]);
};

export default useAppliedMachineCentersGuard;
